import {
  Loader2,
  CheckCircle2,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useStudyBuilderStore } from "@/stores/study-builder-store";
import type { ActiveToolCall } from "@/api/protocol-types";

const TOOL_LABELS: Record<string, string> = {
  save_study_overview: "Saving study overview",
  save_crf: "Building CRF",
  save_visit: "Scheduling visit",
  save_arm: "Configuring treatment arm",
  save_eligibility_criteria: "Defining eligibility criteria",
  save_endpoint: "Setting endpoint",
  save_query_rule: "Creating query rule",
  save_etmf_structure: "Generating eTMF structure",
  search_clinical_trials: "Searching clinical trials",
  lookup_npi_provider: "Looking up provider",
};

function getToolDetail(tool: string, input: Record<string, unknown>): string | null {
  switch (tool) {
    case "save_crf": {
      const name = input.name as string | undefined;
      const sections = input.sections as unknown[] | undefined;
      const fieldCount = sections
        ? (sections as { fields?: unknown[] }[]).reduce((sum, s) => sum + (s.fields?.length ?? 0), 0)
        : 0;
      return name ? `${name}${fieldCount > 0 ? ` (${fieldCount} fields)` : ""}` : null;
    }
    case "save_visit": {
      const name = input.name as string | undefined;
      const studyDay = input.studyDay as number | undefined;
      return name ? `${name}${studyDay != null ? ` (Day ${studyDay})` : ""}` : null;
    }
    case "save_arm": {
      const name = input.name as string | undefined;
      const intervention = input.intervention as string | undefined;
      return name ? `${name}${intervention ? ` — ${intervention}` : ""}` : null;
    }
    case "save_eligibility_criteria": {
      const inclusion = input.inclusionCriteria as unknown[] | undefined;
      const exclusion = input.exclusionCriteria as unknown[] | undefined;
      const inc = inclusion?.length ?? 0;
      const exc = exclusion?.length ?? 0;
      return inc + exc > 0 ? `${inc} inclusion, ${exc} exclusion` : null;
    }
    case "save_endpoint": {
      const type = input.type as string | undefined;
      const name = input.name as string | undefined;
      return name ? `${type ? `${type.charAt(0).toUpperCase() + type.slice(1)}: ` : ""}${name}` : null;
    }
    case "save_study_overview": {
      const title = input.studyTitle as string | undefined;
      return title ? title.slice(0, 60) : null;
    }
    default:
      return null;
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

export function StudyBuildInspector() {
  const isStreaming = useStudyBuilderStore((s) => s.isStreaming);
  const activeToolCalls = useStudyBuilderStore((s) => s.activeToolCalls);
  const currentRound = useStudyBuilderStore((s) => s.currentRound);
  const maxRounds = useStudyBuilderStore((s) => s.maxRounds);
  const retryStatus = useStudyBuilderStore((s) => s.retryStatus);
  const tokenUsage = useStudyBuilderStore((s) => s.tokenUsage);

  // Only render streaming-related content
  if (!isStreaming) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Sparkles className="h-8 w-8 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">
          AI activity will appear here while streaming.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Round progress */}
      {currentRound > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px] font-medium text-muted-foreground">
            <span>Round {currentRound}/{maxRounds}</span>
            <span>{Math.round((currentRound / maxRounds) * 100)}%</span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${(currentRound / maxRounds) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Retry warning */}
      {retryStatus && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            Rate limited — retrying in {Math.round(retryStatus.delayMs / 1000)}s
            (attempt {retryStatus.attempt}/{retryStatus.maxRetries})
          </span>
        </div>
      )}

      {/* Active tool calls */}
      {activeToolCalls.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {activeToolCalls.map((tc, i) => (
            <ToolCallRow key={`${tc.tool}-${i}`} tc={tc} />
          ))}
        </div>
      )}

      {/* Token usage */}
      {tokenUsage && (
        <div className="text-[10px] tabular-nums text-muted-foreground text-center">
          Tokens: {formatNumber(tokenUsage.inputTokens)} in / {formatNumber(tokenUsage.outputTokens)} out
        </div>
      )}
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────

function ToolCallRow({ tc }: { tc: ActiveToolCall }) {
  const detail = getToolDetail(tc.tool, tc.input);

  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2 text-xs",
        tc.status === "running"
          ? "border-primary/30 bg-primary/5 text-primary"
          : "border-border bg-card text-muted-foreground",
      )}
    >
      <div className="flex items-center gap-2">
        {tc.status === "running" ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        ) : (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
        )}
        <span className="truncate font-medium">
          {TOOL_LABELS[tc.tool] ?? tc.tool}
        </span>
      </div>
      {detail && (
        <p className="mt-0.5 ml-5.5 truncate text-[10px] text-muted-foreground">
          {detail}
        </p>
      )}
      {tc.status === "done" && tc.summary && (
        <p className="mt-0.5 ml-5.5 truncate text-[10px] text-muted-foreground/70">
          {tc.summary}
        </p>
      )}
    </div>
  );
}
